import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProfilePicture } from '../utils/profilePicture';
import { generateMatchRosterPDF } from '../utils/pdfRoster';
import api from '../services/api';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';
import LoadingSpinner from '../components/LoadingSpinner';

interface TeamMember {
  id: string;
  role: string;
  position?: string;
  jerseyNumber?: number;
  isGuest: boolean;
  isActive: boolean;
  user: { id: string; firstName: string; lastName: string } | null;
}

interface PlayerStatus {
  canPlay: boolean;
  status: string;
  statusReasons: string[];
  yellowCards: number;
  redCards: number;
  unpaidAmount: number;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  status: string;
  leagueId?: string;
  members: TeamMember[];
  manager: { id: string; firstName: string; lastName: string } | null;
  captain: { id: string; firstName: string; lastName: string } | null;
}

interface League {
  id: string;
  name: string;
}

interface Match {
  id: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  status: string;
  homeTeamId: string;
  awayTeamId: string;
  homeTeam?: { id: string; name: string };
  awayTeam?: { id: string; name: string };
  league?: { id: string; name: string };
  homeScore?: number;
  awayScore?: number;
}

const TeamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [playerStatuses, setPlayerStatuses] = useState<Record<string, PlayerStatus>>({});
  const [league, setLeague] = useState<League | null>(null);
  const [upcomingMatches, setUpcomingMatches] = useState<Match[]>([]);
  const [isAssociated, setIsAssociated] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await api.get(`/teams/${id}`);
        const teamData = response.data;
        setTeam(teamData);
        
        // Fetch league information if team has a leagueId
        if (teamData.leagueId) {
          try {
            const leagueResponse = await api.get(`/leagues/${teamData.leagueId}`);
            setLeague(leagueResponse.data);
          } catch (error) {
            console.error('Error fetching league:', error);
          }
        }
        
        // Check if user is associated with the team
        if (user) {
          const isManager = teamData.manager?.id === user.id;
          const isAdmin = isAdminOrSuperAdmin(user.profileType);
          const isReferee = user.profileType === 'referee';
          
          // Check if user is a member of the team
          let isMember = false;
          if (teamData.members) {
            isMember = teamData.members.some((member: TeamMember) => 
              member.user?.id === user.id
            );
          }
          
          const associated = isManager || isMember || isAdmin || isReferee;
          setIsAssociated(associated);
          
          // Check if user has permission to print roster (manager or admin)
          const hasPermissionToPrint = isManager || isAdmin;
          setHasPermission(hasPermissionToPrint);
          
          // Fetch player statuses for each member (for all users)
          if (teamData.members) {
            const statusPromises = teamData.members.map(async (member: TeamMember) => {
              try {
                const statusRes = await api.get(`/teams/${id}/members/${member.id}/status`);
                return { memberId: member.id, status: statusRes.data };
              } catch (error) {
                return { memberId: member.id, status: null };
              }
            });
            const statusResults = await Promise.all(statusPromises);
            const statusMap: Record<string, PlayerStatus> = {};
            statusResults.forEach(({ memberId, status }) => {
              if (status) {
                statusMap[memberId] = status;
              }
            });
            setPlayerStatuses(statusMap);
          }
        }
        
        // Fetch upcoming matches for all users
        if (id) {
          try {
            const matchesResponse = await api.get('/matches', {
              params: {
                teamId: id,
                status: 'scheduled',
                limit: '100'
              }
            });
            const matches = matchesResponse.data.matches || matchesResponse.data;
            
            // Filter to only upcoming matches (date >= today)
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            const upcoming = matches
              .filter((m: Match) => {
                const matchDate = new Date(m.scheduledDate);
                matchDate.setHours(0, 0, 0, 0);
                return matchDate >= now;
              })
              .sort((a: Match, b: Match) => {
                const dateA = new Date(a.scheduledDate).getTime();
                const dateB = new Date(b.scheduledDate).getTime();
                if (dateA !== dateB) {
                  return dateA - dateB;
                }
                const timeA = a.scheduledTime || '00:00';
                const timeB = b.scheduledTime || '00:00';
                return timeA.localeCompare(timeB);
              });
            
            setUpcomingMatches(upcoming.slice(0, 3));
          } catch (error) {
            console.error('Error fetching upcoming matches:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching team:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [id, user]);

  const handlePrintNextGameRoster = async () => {
    if (!upcomingMatches || upcomingMatches.length === 0) {
      alert('No upcoming games found');
      return;
    }

    const nextMatch = upcomingMatches[0];
    const isHome = nextMatch.homeTeamId === id;
    const teamType: 'home' | 'away' = isHome ? 'home' : 'away';

    try {
      const response = await api.get(`/matches/${nextMatch.id}/roster`);
      const { match, homeTeamPlayers, awayTeamPlayers } = response.data;
      
      await generateMatchRosterPDF(
        {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          scheduledDate: match.scheduledDate,
          scheduledTime: match.scheduledTime,
          location: match.location,
          league: match.league
        },
        homeTeamPlayers,
        awayTeamPlayers,
        teamType
      );
    } catch (error: any) {
      console.error('Error generating roster PDF:', error);
      alert(error.response?.data?.error || 'Failed to generate roster PDF');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!team) {
    return <div className="container">Team not found</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {team.logo ? (
              <img src={team.logo} alt={team.name} style={{ width: '80px', height: '80px', borderRadius: '12px' }} />
            ) : (
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: team.colors.primary,
                  border: `4px solid ${team.colors.secondary}`
                }}
              />
            )}
            <div>
              <h1>{team.name}</h1>
              <p style={{ color: '#757575' }}>{team.teamNumber}</p>
              <span className={`badge ${team.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>
                {team.status}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {hasPermission && upcomingMatches.length > 0 && (
              <button
                className="btn btn-secondary"
                onClick={handlePrintNextGameRoster}
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                📄 Print Next Game Roster
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  const res = await api.get(`/calendar/team/${team.id}`, { responseType: 'blob' });
                  const blob = new Blob([res.data], { type: 'text/calendar' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${team.name}-calendar.ics`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  alert('Failed to download calendar');
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              Export Calendar
            </button>
            {(isAssociated && (team.manager?.id === user?.id || isAdminOrSuperAdmin(user?.profileType))) && (
              <Link to={`/teams/${team.id}/manage`} className="btn btn-primary">
                Manage Team
              </Link>
            )}
          </div>
        </div>

        <div style={{ marginTop: '30px' }}>
          <h2>Team Information</h2>
          <p>
            <strong>Manager:</strong>{' '}
            {team.manager ? (
              <Link to={`/users/${team.manager.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                {team.manager.firstName} {team.manager.lastName} (manager)
              </Link>
            ) : (
              'N/A'
            )}
          </p>
          <p>
            <strong>Captain:</strong>{' '}
            {team.captain ? (
              <Link to={`/users/${team.captain.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                {team.captain.firstName} {team.captain.lastName} (captain)
              </Link>
            ) : (
              'N/A'
            )}
          </p>
          {league && (
            <p>
              <strong>League:</strong>{' '}
              <Link 
                to={`/leagues?leagueId=${league.id}`} 
                style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
              >
                {league.name}
              </Link>
            </p>
          )}
        </div>

        <div style={{ marginTop: '30px' }}>
          <h2>Next Games</h2>
          {upcomingMatches.length === 0 ? (
            <p style={{ color: '#757575' }}>No upcoming matches scheduled.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {upcomingMatches.map((match) => {
                const isHome = match.homeTeamId === id;
                const opponent = isHome ? match.awayTeam : match.homeTeam;
                return (
                  <div
                    key={match.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '15px',
                      background: '#F5F5F5',
                      borderRadius: '8px',
                      border: '1px solid #E0E0E0'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        {isHome ? 'vs' : '@'} {opponent?.name || 'TBD'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#757575' }}>
                        {match.location || 'Location TBD'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: '500' }}>
                        {new Date(match.scheduledDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div style={{ fontSize: '13px', color: '#757575' }}>
                        {match.scheduledTime || ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {team.members && team.members.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <h2>Roster ({team.members.length} players)</h2>
          <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Jersey #</th>
                <th>Name</th>
                <th>Position</th>
                <th>Role</th>
                <th>Type</th>
                <th>Status</th>
                <th>Discipline</th>
              </tr>
            </thead>
            <tbody>
              {team.members.map((member) => {
                const status = playerStatuses[member.id];
                const canPlay = status?.canPlay ?? true;
                const statusReasons = status?.statusReasons || [];
                
                return (
                  <tr key={member.id}>
                    <td>
                      {member.user ? (
                        <img 
                          src={getProfilePicture(member.user)} 
                          alt={`${member.user.firstName} ${member.user.lastName}`}
                          style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#E0E0E0' }} />
                      )}
                    </td>
                    <td>{member.jerseyNumber || '-'}</td>
                    <td>
                      {member.user ? (
                        <Link to={`/users/${member.user.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                          {member.user.firstName} {member.user.lastName}
                        </Link>
                      ) : (
                        'Unknown'
                      )}
                    </td>
                    <td>{member.position || '-'}</td>
                    <td>
                      <span className={`badge ${member.role === 'captain' ? 'badge-info' : 'badge-secondary'}`}>
                        {member.role}
                      </span>
                    </td>
                    <td>
                      {member.isGuest ? (
                        <span className="badge badge-warning">Guest</span>
                      ) : (
                        <span className="badge badge-success">Regular</span>
                      )}
                    </td>
                    <td>
                      {status ? (
                        <div>
                          <span className={`badge ${canPlay ? 'badge-success' : 'badge-danger'}`}>
                            {canPlay ? 'Eligible' : 'Ineligible'}
                          </span>
                          {statusReasons.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#757575', marginTop: '4px' }}>
                              {statusReasons.join(', ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <LoadingSpinner size="small" inline />
                      )}
                    </td>
                    <td>
                      {status ? (
                        <div style={{ fontSize: '14px' }}>
                          {status.yellowCards > 0 && (
                            <span className="badge badge-warning" style={{ marginRight: '5px' }}>
                              🟨 {status.yellowCards}
                            </span>
                          )}
                          {status.redCards > 0 && (
                            <span className="badge badge-danger">
                              🟥 {status.redCards}
                            </span>
                          )}
                          {status.yellowCards === 0 && status.redCards === 0 && (
                            <span style={{ color: '#9E9E9E' }}>No cards</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#9E9E9E' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDetail;

